import React from 'react';
import { useOutletContext } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { Card, CardContent } from '@/components/ui/card';
import { CheckCircle2 } from 'lucide-react';

export default function AboutPage() {
  const { t } = useLanguage();
  const { company } = useOutletContext() || {};

  const facts = [
    { label: 'Company', value: company?.name || 'Concorde Financial Limited' },
    { label: 'Company N°', value: company?.number || '16195' },
    { label: 'License', value: company?.license || 'L16195/CFL' },
    { label: 'Regulator', value: company?.regulator || 'Anjouan Offshore Finance Authority' },
    { label: 'Jurisdiction', value: company?.jurisdiction || 'Autonomous Island of Anjouan, Union of Comoros' },
    { label: 'Address', value: company?.address || 'Boulevard de Coalancanthe, Mutsamudu, Anjouan' },
    { label: t('about.director'), value: company?.director || 'Aleksejs Kozirevs' },
    { label: 'Incorporation', value: company?.incorporation_date || '2025-11-03' },
    { label: t('about.license_valid_until'), value: company?.license_valid_until || '2026-11-03' },
  ];

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-16">
      <div className="text-xs uppercase tracking-widest text-[hsl(var(--brand-gold))]">About</div>
      <h1 className="font-serif text-4xl sm:text-5xl mt-2 text-[hsl(var(--brand-navy-2))]">{t('about.title')}</h1>
      <p className="mt-6 text-lg text-[hsl(var(--muted-foreground))] max-w-3xl">{t('about.lead')}</p>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mt-12">
        <div className="lg:col-span-7 space-y-5 text-[hsl(var(--foreground))] leading-7">
          <p><strong>Concorde Financial Limited operates exclusively as an international brokerage and clearing house.</strong> The Company is authorised solely to conduct brokerage activities under an International Brokerage and Clearing House License N° L16195/CFL, issued by the Anjouan Offshore Finance Authority pursuant to Government Notice N° 005 of 2005 and the International Business Companies Act 004 of 2005.</p>
          <p>All services offered by the Company — including multi-currency client accounts, currency conversion between funding wallets, corporate onboarding and invoicing — are provided <strong>strictly within the scope of brokerage operations</strong>. The Company does not conduct banking, does not extend credit, does not provide investment advisory on a discretionary basis, and does not manage assets.</p>
          <p>The Company operates from its registered office at Boulevard de Coalancanthe, Mutsamudu, Anjouan, Union of Comoros. It benefits from a full tax exemption on worldwide income and assets within Anjouan.</p>
          <h2 className="font-serif text-2xl mt-8 text-[hsl(var(--brand-navy-2))]">{t('about.authorized_activities')}</h2>
          <p className="text-sm text-[hsl(var(--muted-foreground))] italic">Under License N° L16195/CFL, the Company is authorised to conduct the following brokerage-only activities:</p>
          <ul className="space-y-2">
            {(company?.activities || []).map((a, i) => (
              <li key={i} className="flex items-start gap-2">
                <CheckCircle2 className="h-4 w-4 text-[hsl(var(--brand-gold))] mt-1 flex-shrink-0" />
                <span className="text-sm">{a}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="lg:col-span-5">
          <Card className="bg-white sticky top-24" data-testid="about-company-facts">
            <CardContent className="p-6">
              <div className="text-xs uppercase tracking-widest text-[hsl(var(--brand-gold))]">{t('about.company_facts')}</div>
              <dl className="mt-4 space-y-3">
                {facts.map((f, i) => (
                  <div key={i} className="grid grid-cols-3 gap-2 text-sm border-b border-border/50 pb-2 last:border-0">
                    <dt className="text-[hsl(var(--muted-foreground))]">{f.label}</dt>
                    <dd className="col-span-2 font-medium text-right">{f.value}</dd>
                  </div>
                ))}
              </dl>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
